import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

/**
 * Mobile-only back button rendered inside the app Layout. Shows on every
 * internal page and hides on:
 * - The home / root ("/") — nothing to go back to.
 * - Checkout flow — has its own step-based UI.
 * - Auth / callback pages that render full-screen.
 *
 * Behavior: `navigate(-1)` when there's a real history entry; otherwise
 * falls back to `/` so the button always leads somewhere sensible even on
 * hard-reloaded deep-links.
 */
export default function MobileBackButton() {
  const { language } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  // Routes that should NEVER show the mobile back arrow.
  const HIDDEN = [
    '/',
    '/status',
    '/auth/google/callback',
  ];
  const HIDDEN_PREFIXES = [
    '/checkout',
    '/select-city',       // Has its own back button baked into the page.
    '/business/reception',
    '/legal-verify',
  ];

  if (HIDDEN.includes(location.pathname)) return null;
  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;

  return (
    <div
      className="md:hidden sticky top-0 z-30 bg-[#FAF7F2]/95 dark:bg-[#12100E]/95 backdrop-blur-md border-b border-[#EAE3D9]/70 dark:border-[#332D29]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        onClick={() => {
          if (window.history.length > 1) navigate(-1);
          else navigate('/');
        }}
        className="inline-flex items-center gap-1.5 h-11 pl-3 pr-4 rounded-full text-[13.5px] font-semibold text-[#2A2A2A] dark:text-[#F5F2ED] active:bg-[#EAE3D9] dark:active:bg-[#2A2522] transition-colors"
        data-testid="mobile-back-button"
        aria-label={language === 'es' ? 'Regresar' : 'Back'}
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
        <span>{language === 'es' ? 'Regresar' : 'Back'}</span>
      </button>
    </div>
  );
}
